import { PredicateAnswerType, PredicateOperator, type ProjectionView } from '@data/sdk/match';

/** How many values the `value` field holds for a given operator × answerType. */
export type ValueArity = 'scalar' | 'range' | 'array' | 'null';

const ALL_TYPES: PredicateAnswerType[] = Object.values(PredicateAnswerType);

/**
 * Mirrors `getPredicateValueSchema` in `packages/shared-contracts/src/predicateValue.schema.ts`,
 * the single source of truth for which operator × answerType combinations are legal and what
 * shape their value takes. Kept by hand because the web app has no zod, so the schema itself
 * cannot be imported - **check this table against that file whenever either changes.**
 *
 * Shape only. The schema also enforces constraints no arity can express (a range's first element
 * must not exceed its second, arrays and strings must be non-empty, dates must parse), so a value
 * that passes here can still come back as `InvalidPredicateValue`, which lands on the field.
 */
const SUPPORTED: {
  arity: ValueArity;
  operators: PredicateOperator[];
  types: PredicateAnswerType[];
}[] = [
  {
    // No boolean: a yes/no fact says presence, which is `exists` / `not_exists`.
    operators: [PredicateOperator.Eq, PredicateOperator.Neq],
    types: ALL_TYPES.filter((type) => type !== PredicateAnswerType.Boolean),
    arity: 'scalar',
  },
  {
    operators: [
      PredicateOperator.Gt,
      PredicateOperator.Gte,
      PredicateOperator.Lt,
      PredicateOperator.Lte,
    ],
    types: [PredicateAnswerType.Number, PredicateAnswerType.Date],
    arity: 'scalar',
  },
  {
    operators: [PredicateOperator.Between],
    types: [PredicateAnswerType.Number, PredicateAnswerType.Date],
    arity: 'range',
  },
  {
    operators: [PredicateOperator.In, PredicateOperator.NotIn],
    types: [
      PredicateAnswerType.Number,
      PredicateAnswerType.Enum,
      PredicateAnswerType.String,
      PredicateAnswerType.Date,
    ],
    arity: 'array',
  },
  {
    operators: [PredicateOperator.Contains],
    types: [PredicateAnswerType.String],
    arity: 'scalar',
  },
  {
    operators: [PredicateOperator.Exists, PredicateOperator.NotExists],
    types: ALL_TYPES,
    arity: 'null',
  },
];

const keyOf = (operator: string, answerType: string) => `${operator}__${answerType}`;

const ARITY_BY_KEY = new Map<string, ValueArity>();
SUPPORTED.forEach(({ operators, types, arity }) =>
  operators.forEach((operator) =>
    types.forEach((type) => ARITY_BY_KEY.set(keyOf(operator, type), arity)),
  ),
);

/** Value arity for an operator × answerType, or null when the combination is unsupported. */
export const valueArityOf = (
  operator: PredicateOperator,
  answerType: PredicateAnswerType,
): ValueArity | null => ARITY_BY_KEY.get(keyOf(operator, answerType)) ?? null;

/** Operators that produce a legal value shape for the given answer type. */
export const supportedOperatorsFor = (answerType: PredicateAnswerType): PredicateOperator[] =>
  SUPPORTED.filter((entry) => entry.types.includes(answerType)).flatMap((entry) => entry.operators);

/**
 * Filled in enough to be worth sending. `NaN` counts as absent because it is what a non-numeric
 * entry becomes and it serialises to `null`, which the schema then refuses.
 */
const isPresent = (entry: unknown): boolean =>
  entry !== null && entry !== undefined && entry !== '' && !Number.isNaN(entry);

/**
 * Whether a stored value fits its operator's arity. Shape-level stand-in for the server's schema
 * parse, used to decide whether a row can be approved without opening the drawer - the server
 * validates the same pair and refuses the request otherwise.
 *
 * Presence counts as shape: a half-filled range is still a two-element tuple, and the server
 * refuses it, so a footer that only counted elements would offer an approval it could not deliver.
 */
export const isValueShapeValid = (
  operator: PredicateOperator,
  answerType: PredicateAnswerType,
  value: unknown,
): boolean => {
  const arity = valueArityOf(operator, answerType);
  if (arity === null) return false;
  if (arity === 'null') return value === null || value === undefined;
  if (!isPresent(value)) return false;
  if (arity === 'range') {
    return Array.isArray(value) && value.length === 2 && value.every(isPresent);
  }
  if (arity === 'array') {
    return Array.isArray(value) && value.length > 0 && value.every(isPresent);
  }
  return !Array.isArray(value);
};

/**
 * The answer shape a value is validated against. The projection owns it - a `count` projection is
 * a number even on a boolean-ish category, and the category carries no shape of its own. Same
 * resolution the server and the engine use. Undefined until a projection is picked, which is what
 * makes the value control wait for one.
 */
export const answerTypeOf = (
  projection: ProjectionView | null | undefined,
): PredicateAnswerType | undefined =>
  (projection?.answerType ?? undefined) as PredicateAnswerType | undefined;
